import { useEffect, useState } from 'react';
import { Button, IconName, StackItem } from '@optimus-web/core';
import { FloatingPanel } from '@mews/b2b-ui';
import { LocalizedText, useDialog } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { OptimusProvider, UnsavedChangesDialog } from '@mews-commander/ui';
import { EmailValidationProvider } from '@mews-commander/form';
import { useAuthorizationCheck, useIsCheckingAuthValidity } from '../../hooks/useAuthorizationCheck';
import { AddUsersForm } from './AddUsersForm';
import { AddUsersSuccess } from './AddUsersSuccess';
import { useAddUsersForm } from './useAddUsersForm';

interface AddUsersDialogProps {
    active: boolean;
    onClose: () => void;
}

export const AddMultipleUsersButton = () => {
    const [dialogProps, toggleDialog] = useDialog();
    const openAddUsersDialog = useAuthorizationCheck(toggleDialog);
    const isCheckingActionValidity = useIsCheckingAuthValidity();

    return (
        <StackItem>
            <Button
                icon={IconName.AddCustomer}
                onClick={openAddUsersDialog}
                variant="tertiary"
                type="submit"
                loading={isCheckingActionValidity}
            >
                <LocalizedText textKey={TextKey.InviteUsers} />
            </Button>

            <EmailValidationProvider>
                <AddUsersFormDialog {...dialogProps} />
            </EmailValidationProvider>
        </StackItem>
    );
};

const AddUsersFormDialog = ({ active, onClose }: AddUsersDialogProps) => {
    const [{ active: showUnsavedChangesDialog }, toggleUnsavedChangesDialog] = useDialog();
    const [emailsSent, setEmailsSent] = useState<string[]>([]);

    const { renderFormContent, submit, isPending, reset, hasDirtyFields } = useAddUsersForm({ setEmailsSent });

    const isSuccessStep = emailsSent.length;

    const handleClose = () => {
        if (hasDirtyFields && !isSuccessStep) {
            toggleUnsavedChangesDialog();
        } else {
            onClose();
        }
    };

    useEffect(() => {
        reset();
        setEmailsSent([]);
    }, [active, reset]);

    return renderFormContent(
        <OptimusProvider density="spacious">
            <FloatingPanel
                active={active}
                footer={{
                    primary: isSuccessStep ? (
                        <Button type="button" variant="primary" onClick={onClose}>
                            <LocalizedText textKey={TextKey.Close} />
                        </Button>
                    ) : (
                        <Button type="submit" variant="primary" onClick={submit}>
                            <LocalizedText textKey={TextKey.SendInvitePlural} />
                        </Button>
                    ),
                }}
                loading={isPending}
                onClose={handleClose}
                title={<LocalizedText textKey={TextKey.InviteNewUsers} />}
            >
                {isSuccessStep ? <AddUsersSuccess emailsSent={emailsSent} /> : <AddUsersForm />}
            </FloatingPanel>
            <UnsavedChangesDialog
                active={showUnsavedChangesDialog}
                onCancel={toggleUnsavedChangesDialog}
                onDiscardChanges={() => {
                    onClose();
                    toggleUnsavedChangesDialog();
                }}
            />
        </OptimusProvider>
    );
};
